import { useState, useEffect } from "react";
import {
  UserPlus,
  ShieldOff,
  ShieldCheck,
  Clock,
  Loader2,
} from "lucide-react";
import { supabase } from "../lib/supabase";

// Historial de acceso del alumno (Fase 6.5).
// Lee los eventos reales de student_access_events (el admin puede por la policy
// is_admin()) y añade un hito visual "Caducado" derivado de access_expires_at.
// Solo lectura: no escribe nada.

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// Estilo por tipo de evento, en vocabulario ETC ("alumno").
const EVENT_META = {
  invited: {
    label: "Iniciado",
    icon: UserPlus,
    color: "text-indigo-500",
  },
  revoked: {
    label: "Acceso retirado",
    icon: ShieldOff,
    color: "text-red-500",
  },
  restored: {
    label: "Acceso rehabilitado",
    icon: ShieldCheck,
    color: "text-emerald-500",
  },
  expired: {
    label: "Caducado",
    icon: Clock,
    color: "text-amber-500",
  },
};

// Añade el hito derivado "Caducado" si procede: la caducidad está en el pasado
// y no hay evento revoked/restored posterior a esa fecha. Solo visual.
// DEDUP (Fase 6.6): si ya existe una fila real 'expired', no añadimos el
// derivado (la caducidad ya quedó persistida al rehabilitar). En la práctica no
// coinciden, pero dejamos la guarda por seguridad.
function withDerivedExpiry(events, student) {
  const expiresAt = student?.access_expires_at;
  if (!expiresAt) return events;

  const hasRealExpired = events.some((e) => e.event_type === "expired");
  if (hasRealExpired) return events;

  const expiryTime = new Date(expiresAt).getTime();
  if (Number.isNaN(expiryTime) || expiryTime >= Date.now()) return events;

  const hasLaterOverride = events.some(
    (e) =>
      (e.event_type === "revoked" || e.event_type === "restored") &&
      new Date(e.created_at).getTime() > expiryTime
  );
  if (hasLaterOverride) return events;

  return [
    ...events,
    { id: "derived-expired", event_type: "expired", created_at: expiresAt },
  ];
}

function authorLabel(event) {
  // La caducidad y el seed no tienen actor: los hace el sistema.
  if (event.actor_name) return `por ${event.actor_name}`;
  return "sistema";
}

export default function StudentAccessTimeline({ student }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!student?.id) return;
    let active = true;
    setLoading(true);

    supabase
      .from("student_access_events")
      .select("id, event_type, actor_name, actor_email, created_at")
      .eq("student_id", student.id)
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (!active) return;
        if (error) console.error("[timeline] load failed:", error);
        setEvents(data || []);
        setLoading(false);
      });

    return () => {
      active = false;
    };
    // Refresca tras revoke/restore: el padre actualiza estas columnas.
  }, [student?.id, student?.access_revoked_at, student?.access_expires_at]);

  // Mezcla eventos reales + hito derivado y ordena como una historia (asc).
  const timeline = withDerivedExpiry(events, student).sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <h3 className="text-sm font-semibold text-gray-900">Historial de permisos</h3>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Loader2 size={16} className="animate-spin" />
          Cargando historial…
        </div>
      ) : timeline.length === 0 ? (
        <p className="text-sm text-gray-400">Sin eventos de permisos.</p>
      ) : (
        <ol className="space-y-3">
          {timeline.map((event) => {
            const meta = EVENT_META[event.event_type];
            const Icon = meta.icon;
            const author = authorLabel(event);
            return (
              <li key={event.id} className="flex items-start gap-3">
                <Icon size={16} className={`shrink-0 mt-0.5 ${meta.color}`} />
                <div className="text-sm">
                  <p className="font-medium text-gray-900">{meta.label}</p>
                  <p className="text-gray-500">
                    {formatDate(event.created_at)}
                    {author && <span className="ml-1">· {author}</span>}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
